import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import get from 'lodash/get';

// Types
import { CreateAccountFlowType } from './types';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Helpers
import { isConsumerDomain, validEmail } from 'Shared/helpers';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useOnboardingState } from 'Shared/utils/hooks/use-onboarding-state';
import { useApp } from 'Shared/utils/hooks/use-app';

// Components
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';
import WpButton from 'Wpds/components/button/WpButton';
import { VisuallyHidden } from 'Wpds/components/visually-hidden/VisuallyHidden';
import WpAlert from 'Wpds/components/alert/WpAlert';
import { WpCollapsible } from 'Wpds/components/collapsible';
import { WpButtonGroup } from 'Wpds/components/button';
import OAuthButton from 'Shared/components/OAuthButton/OAuthButton';

// Styles
import './SignUpPerformForm.scss';

type Props = {
  flowType: CreateAccountFlowType;
  askForName?: boolean;
  piCompanyGuid?: string;
  askForRole?: boolean;
  inviterSlug?: string;
  workspaceSlug?: string;
  currentEmail?: string;
  invitationToken?: string;
  withCalendar?: boolean;
};

const SignUpPerformForm = ({
  flowType,
  askForName = false,
  askForRole = false,
  inviterSlug,
  workspaceSlug,
  currentEmail,
  invitationToken,
  withCalendar = true,
}: Props) => {
  const { search } = useLocation();
  const {
    app: {
      routes: { signUpUrl, userInvitePath },
    },
  } = useApp();

  const { onboardingState, handleUpdateOnboardingState } = useOnboardingState();
  const submitBtnRef = useRef(null);

  const [isShowingEmailForm, toggleIsShowingEmailForm] = useToggle(false);
  const [isSubmittingEmailForm, toggleIsSubmittingEmailForm] = useToggle(false);

  const hasWorkEmailError = search.includes(QueryParamConstants.WORK_EMAIL_ERROR);

  useEffect(() => {
    if (hasWorkEmailError) {
      toggleIsShowingEmailForm();
    }
  }, []);

  const actionPath = flowType === CreateAccountFlowType.acceptInviteFromEmail ? userInvitePath : signUpUrl;

  const additionalQueryParams = `${inviterSlug ? `&inviter_slug=${inviterSlug}` : ''}${
    workspaceSlug ? `&workspace_slug=${workspaceSlug}` : ''
  }${invitationToken ? `&invitation_token=${invitationToken}` : ''}`;

  const calendarEventName = 'Connected calendar (onboarding)';
  const stepName = 'CreateAccount';

  const handleTrackSignUpWithGoogle = () => {
    (window as any).analytics.track(calendarEventName, {
      calendar_type: 'google',
      onboarding_step: stepName,
    });
  };

  const handleTrackSignUpWithOffice365 = () => {
    (window as any).analytics.track(calendarEventName, {
      calendar_type: 'office365',
      onboarding_step: stepName,
    });
  };

  const handleSubmit = () => {
    toggleIsSubmittingEmailForm();
  };

  const handleEmailButtonClick = () => {
    if (isShowingEmailForm) submitBtnRef.current.click();
    else toggleIsShowingEmailForm();
  };

  const handleSaveRole = value => {
    if (value !== onboardingState?.profile?.role)
      handleUpdateOnboardingState({
        onboardingState: {
          profile: {
            role: value,
          },
        },
      });
  };

  const isValidEmail = value => {
    const isValidEmail = validEmail(value);
    const consumerDomain = isConsumerDomain(value);
    return isValidEmail && !consumerDomain;
  };

  const baseClassName = 'SignUpPerformForm';
  const csrfValue = get(document.getElementsByName('csrf-token')[0], 'content');

  return (
    <div className={baseClassName}>
      {askForRole && (
        <div className={`${baseClassName}__role`}>
          <p className={`${baseClassName}__role__label`}>At Work, I am...</p>
          <WpRadioGroup
            id="role_input"
            label="At Work, I am"
            direction="horizontal"
            required
            showRequiredIndicator={false}
            onChange={handleSaveRole}
            defaultValue={onboardingState?.profile?.role || 'manager'}
            labelHidden
          >
            <WpRadioInput id="manager" label="A Manager" value="manager" />
            <WpRadioInput id="full_member" label="An individual contributor" value="full_member" />
          </WpRadioGroup>
        </div>
      )}
      <WpButtonGroup stacked>
        <OAuthButton
          oauthProvider="google"
          text={`Sign up with Google${withCalendar ? ' Calendar' : ''}`}
          withCalendar={withCalendar}
          color="primary"
          variant={isShowingEmailForm ? 'secondary' : 'primary'}
          onClick={handleTrackSignUpWithGoogle}
          additionalQueryParams={additionalQueryParams}
        />
        <OAuthButton
          oauthProvider="office365"
          text={`Sign up with Office365${withCalendar ? ' Calendar' : ''}`}
          withCalendar={withCalendar}
          color="primary"
          variant={isShowingEmailForm ? 'secondary' : 'primary'}
          onClick={handleTrackSignUpWithOffice365}
          additionalQueryParams={additionalQueryParams}
        />
        <div className={`${baseClassName}__collapsible`}>
          <WpCollapsible open={isShowingEmailForm} animationDuration={400}>
            <p className={`${baseClassName}__collapsible__title`}>Sign up with email & password</p>
            <WpForm onSubmit={handleSubmit} method="post" action={actionPath}>
              <WpTextField
                id="authenticity_token"
                name="authenticity_token"
                label="authenticity_token"
                type="hidden"
                value={csrfValue}
              />
              <WpFormLayout spacing="md">
                {askForName && (
                  <WpTextField
                    name="name"
                    id="user[name]"
                    type="text"
                    label="Full name"
                    placeholder=""
                    required
                    showRequiredIndicator={false}
                    disabled={isSubmittingEmailForm}
                    autoFocus
                  />
                )}
                <WpTextField
                  name="email"
                  id="user[email]"
                  type="text"
                  label="Work email"
                  placeholder=""
                  required
                  validate={{
                    isEmail: {
                      callback: isValidEmail,
                      error: 'Please enter a valid work email.',
                    },
                  }}
                  value={currentEmail}
                  showRequiredIndicator={false}
                  disabled={!!currentEmail || isSubmittingEmailForm}
                  autoFocus={!askForName}
                />
                <WpTextField
                  name="password"
                  id="user[password]"
                  type="password"
                  label="Password"
                  placeholder=""
                  required
                  minLength={6}
                  disabled={isSubmittingEmailForm}
                  showRequiredIndicator={false}
                />
              </WpFormLayout>
              {inviterSlug && (
                <WpTextField
                  label="inviter_slug"
                  id="inviter_slug"
                  name="inviter_slug"
                  value={inviterSlug}
                  readOnly
                  type="hidden"
                />
              )}
              {workspaceSlug && (
                <WpTextField
                  label="workspace_slug"
                  id="workspace_slug"
                  name="workspace_slug"
                  value={workspaceSlug}
                  readOnly
                  type="hidden"
                />
              )}
              {invitationToken && (
                <WpTextField
                  label="user[invitation_token]"
                  id="user[invitation_token]"
                  name="user[invitation_token]"
                  value={invitationToken}
                  type="hidden"
                />
              )}
              {invitationToken && <WpTextField label="_method" id="_method" name="_method" value="put" type="hidden" />}
              {hasWorkEmailError && !isSubmittingEmailForm && (
                <WpFormLayout>
                  <WpAlert type="danger">Please sign up with a work email address.</WpAlert>
                </WpFormLayout>
              )}
              <VisuallyHidden>
                <WpButton
                  size="lg"
                  btnType="submit"
                  block
                  loading={isSubmittingEmailForm}
                  qaTest="sign-up-with-email-submit"
                  ref={submitBtnRef}
                >
                  Sign up with work email & password
                </WpButton>
              </VisuallyHidden>
            </WpForm>
          </WpCollapsible>
        </div>
        <WpButton
          variant={isShowingEmailForm ? 'primary' : 'tertiary'}
          size="lg"
          block
          onClick={handleEmailButtonClick}
          qaTest="sign-up-with-email"
          loading={isSubmittingEmailForm}
        >
          {isShowingEmailForm ? 'Sign up with email' : 'Sign up with work email & password'}
        </WpButton>
      </WpButtonGroup>
    </div>
  );
};

export default SignUpPerformForm;
